/**
 * AuthGateModal.jsx — Global gate modal (SPEC-2026-06-29-auth-confirm-2fa.v1).
 *
 * Mounted ONCE in the header (next to SessionTimeoutModal). It reads the Redux
 * `authGate` state, which the encrypted client (apiCallWithToken) dispatches
 * automatically on a 403 carrying an EMAIL_NOT_VERIFIED or TOTP_ENROLL_REQUIRED
 * code — same mechanism as the 401 → SessionTimeoutModal flow.
 *
 * Two self-contained flows :
 *   - EMAIL_NOT_VERIFIED  → attach email (if none) then verify a 6-digit code.
 *       On success : update localStorage `_user` (isVerified + new accessToken),
 *       dispatch setUser(updatedUser), close the gate.
 *   - TOTP_ENROLL_REQUIRED → explain + route the user to Settings 2FA pane.
 *       Enrollment itself happens in Settings (never inline here).
 *
 * The close cross always clears the gate (setAuthGate(null)).
 *
 * NOTE : the TOTP_REQUIRED inline checkout challenge is handled separately and is
 * intentionally NOT covered here (it must replay the original request with a code).
 *
 * §11/§18 — composes ModalShell + ModalDA primitives, theme tokens only.
 * §12sexies — circled close cross top-right. §64 — English UI. §89 — no em-dash.
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as actions from '../../store/actions';
import { readStoredUser, writeStoredUser } from '../../utils/auth/userStorage';
import * as AuthService from '../../services/AuthService';
import * as TwoFactorService from '../../services/TwoFactorService';
import { EPATHS } from '../../routes/paths.enum';
import { COLORS, FONT_SIZE, FONT_WEIGHT } from '../../components/ui/theme';
import { ModalShell } from '../../components/ui/ModalShell';
import {
  ModalHeader,
  ModalHeaderTitle,
  ModalCloseBtn,
  ModalBody,
  ModalFooter,
  ModalIconBadge,
  ButtonPrimary,
  ButtonSecondary,
  FormRow,
  FormLabel,
  FormInput,
} from '../../components/ui/ModalDA';

const Title = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  text-align: center;
`;

const HelperText = styled.p`
  margin: 0 0 1rem;
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.muted};
  text-align: center;
  line-height: 1.45;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: ${COLORS.danger};
  text-align: center;
`;

const ResendConfirm = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: ${COLORS.cta};
  text-align: center;
  line-height: 1.45;
`;

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polyline points="22 6 12 13 2 6" />
  </svg>
);

// readStoredUser importé de utils/auth/userStorage (source unique localStorage._user).

function AuthGateModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const gate = useSelector((state) => state.user.authGate);

  // EMAIL flow state
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resent, setResent] = useState(false);

  const type = gate?.type;
  const hasEmail = !!gate?.hasEmail;

  // Reset local state whenever the gate (re)opens.
  useEffect(() => {
    if (!gate) return;
    setErrorMsg('');
    setResent(false);
    setCode('');
    setEmail('');
    // If the account already has an email on file, skip straight to code entry.
    setStep(type === 'EMAIL_NOT_VERIFIED' && hasEmail ? 'code' : 'email');
  }, [gate, type, hasEmail]);

  const handleClose = () => {
    dispatch(actions.setAuthGate(null));
  };

  const handleSendEmail = async () => {
    const value = email.trim();
    if (!value) {
      setErrorMsg(t('AuthGateEmailRequired'));
      return;
    }
    setBusy(true);
    setErrorMsg('');
    try {
      await TwoFactorService.attachEmail(value);
      setStep('code');
    } catch (err) {
      setErrorMsg(typeof err === 'object' && err?.message ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setBusy(true);
    setErrorMsg('');
    try {
      const stored = readStoredUser() || {};
      // Resend uses the existing AuthService.resend flow (keyed by wallet).
      await AuthService.resend({ metamask: stored.metamask });
      setResent(true);
    } catch (err) {
      setErrorMsg(typeof err === 'object' && err?.message ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    const value = code.trim();
    if (!value) {
      setErrorMsg(t('AuthGateCodeRequired'));
      return;
    }
    setBusy(true);
    setErrorMsg('');
    try {
      const stored = readStoredUser() || {};
      // Reuse the existing verify endpoint (same call as CodeVerification modal).
      const res = await AuthService.verify({
        metamask: stored.metamask,
        code: value,
        isRegister: false,
      });
      // Merge the verified projection into the stored user (preserve any field the
      // verify response might omit) + flag isVerified + refresh accessToken.
      const updatedUser = {
        ...stored,
        ...res,
        isVerified: true,
        ...(res && res.accessToken ? { accessToken: res.accessToken } : {}),
      };
      writeStoredUser(updatedUser);
      dispatch(actions.setUser(updatedUser));
      handleClose();
    } catch (err) {
      setErrorMsg(t('AuthGateCodeInvalid'));
    } finally {
      setBusy(false);
    }
  };

  const goToTwoFactorSettings = () => {
    handleClose();
    // Route to Settings, anchoring the single-pane navigation to the 2FA pane.
    history.push(`${EPATHS.SETTINGS}#two-factor`);
  };

  if (!gate) return null;

  /* ── TOTP enroll required ──────────────────────────────────────────────── */
  if (type === 'TOTP_ENROLL_REQUIRED') {
    return (
      <ModalShell isOpen onClose={handleClose} $width="32rem" role="dialog" aria-label="Two-factor authentication required">
        <ModalHeader>
          <ModalHeaderTitle>{t('AuthGate2faRequiredTitle')}</ModalHeaderTitle>
          <ModalCloseBtn onClick={handleClose} aria-label={t('Close')}>✕</ModalCloseBtn>
        </ModalHeader>
        <ModalBody>
          <ModalIconBadge $bg={COLORS.accentSoft} $border $size="5rem" $mb="1.25rem">
            <ShieldIcon />
          </ModalIconBadge>
          <Title>{t('AuthGate2faRequiredHeading')}</Title>
          <HelperText>
            {t('AuthGate2faRequiredBody')}
            {gate.thresholdUsd != null && (
              <>
                {' '}
                {t('AuthGate2faThreshold', { amount: gate.thresholdUsd })}
              </>
            )}
          </HelperText>
        </ModalBody>
        <ModalFooter>
          <ButtonSecondary onClick={handleClose}>{t('AuthGateNotNow')}</ButtonSecondary>
          <ButtonPrimary onClick={goToTwoFactorSettings}>{t('AuthGateEnable2fa')}</ButtonPrimary>
        </ModalFooter>
      </ModalShell>
    );
  }

  /* ── Email not verified ────────────────────────────────────────────────── */
  return (
    <ModalShell isOpen onClose={handleClose} $width="32rem" role="dialog" aria-label="Verify your email">
      <ModalHeader>
        <ModalHeaderTitle>{t('AuthGateVerifyEmailTitle')}</ModalHeaderTitle>
        <ModalCloseBtn onClick={handleClose} aria-label={t('Close')}>✕</ModalCloseBtn>
      </ModalHeader>
      <ModalBody>
        <ModalIconBadge $bg={COLORS.accentSoft} $border $size="5rem" $mb="1.25rem">
          <MailIcon />
        </ModalIconBadge>
        {step === 'email' ? (
          <>
            <Title>{t('AuthGateAddEmailHeading')}</Title>
            <HelperText>{t('AuthGateAddEmailBody')}</HelperText>
            <FormRow>
              <FormLabel htmlFor="authGateEmailInput">{t('AuthGateEmailLabel')}</FormLabel>
              <FormInput
                id="authGateEmailInput"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('AuthGateEmailPlaceholder')}
              />
              {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
            </FormRow>
          </>
        ) : (
          <>
            <Title>{t('AuthGateEnterCodeHeading')}</Title>
            <HelperText>{t('AuthGateEnterCodeBody')}</HelperText>
            <FormRow>
              <FormLabel htmlFor="authGateCodeInput">{t('Code')}</FormLabel>
              <FormInput
                id="authGateCodeInput"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('EnterCodePlaceholder')}
              />
              {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
            </FormRow>
            {resent && <ResendConfirm>{t('EmailResendConfirm')}</ResendConfirm>}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {step === 'email' ? (
          <ButtonPrimary onClick={handleSendEmail} disabled={busy}>
            {busy ? t('SendingDots') : t('AuthGateSendCode')}
          </ButtonPrimary>
        ) : (
          <>
            <ButtonSecondary onClick={handleResend} disabled={busy}>{t('Resend')}</ButtonSecondary>
            <ButtonPrimary onClick={handleVerify} disabled={busy}>
              {busy ? t('VerifyingDots') : t('Verify')}
            </ButtonPrimary>
          </>
        )}
      </ModalFooter>
    </ModalShell>
  );
}

export default AuthGateModal;
